import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import SubpageHeader from "../components/SubpageHeader";
import SiteFooter from "../components/SiteFooter";
import LoadingSpinner from "../components/LoadingSpinner";
import BackToTopButton from "../components/BackToTopButton";
import BridgeText from "../components/BridgeText";
import Button from "../components/ui/Button";
import TasteTag from "../components/ui/TasteTag";
import Typography from "../components/ui/Typography";
import { fetchMostRecommendedShows } from "../services/backendApi";
import { handleInternalLinkClick } from "../utils/navigation";
import { trackEvent } from "../utils/analytics";
import { cleanBridgeText, firstCompleteSentence } from "../utils/bridgeText";
import { resolveInitialPayload } from "../utils/initialPayload";
import usePageMeta from "../hooks/usePageMeta";
import { formatDisplayDate } from "../utils/formatDisplayDate";
import { buildMostRecommendedSeoMeta } from "./most-recommended/mostRecommendedSeo";
import RecommendationsHub from "./most-recommended/RecommendationsHub";
import "./most-recommended/mostRecommendedMotion.css";

const MOST_RECOMMENDED_LIMIT = 20;

function RecommendationCountLabel({ count }) {
  const value = Number(count) || 0;
  return (
    <>
      Recommended to fans of{" "}
      <span className="text-secondary-readable">{value}</span>
      {value === 1 ? " show" : " shows"}
    </>
  );
}

RecommendationCountLabel.propTypes = {
  count: PropTypes.number,
};

// Summaries are written as lower-case phrases; the page shows them as sentences.
function toSentence(text) {
  const value = String(text || "").trim();
  if (!value) return "";
  const sentence = `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
  return /[.!?]$/.test(sentence) ? sentence : `${sentence}.`;
}

function consensusSummary(show) {
  const essence = typeof show?.semanticEssence?.essence === "string"
    ? show.semanticEssence.essence.trim()
    : "";
  if (essence) return essence;

  const source = Array.isArray(show?.recommendedBy) ? show.recommendedBy[0] : null;
  const bridge = cleanBridgeText(source?.bridge, source?.title, show?.title);
  const sentence = firstCompleteSentence(bridge, [source?.title, show?.title]);
  if (sentence) return sentence;

  if (source?.title) {
    return `Often recommended when viewers ask what to watch after ${source.title}.`;
  }
  return "Recommended repeatedly from different starting shows.";
}

function selectDisplayTasteTags(tags = [], limit = 4) {
  return tags
    .filter(Boolean)
    .slice(0, limit)
}

function getStructuredDataImage(show) {
  return show?.posterMetadata?.thumbnail || show?.poster || show?.omdbData?.poster || null;
}

function getStructuredDataDescription(show) {
  if (typeof show?.semanticAppeal === "string" && show.semanticAppeal.trim()) {
    return show.semanticAppeal.trim();
  }
  if (typeof show?.summary === "string" && show.summary.trim()) {
    return show.summary.trim();
  }
  if (typeof show?.plotOverview === "string" && show.plotOverview.trim()) {
    return show.plotOverview.trim();
  }
  if (typeof show?.omdbData?.plot === "string" && show.omdbData.plot.trim()) {
    return show.omdbData.plot.trim();
  }
  if (Array.isArray(show?.recommendedBy) && show.recommendedBy.length > 0) {
    return `${show.title} is frequently recommended from related PremiereLens picks.`;
  }
  return "";
}

const MostRecommendedStructuredData = ({ shows }) => {
  if (!Array.isArray(shows) || shows.length === 0) return null;

  const itemListJson = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Top 20 Most Recommended TV Shows",
    description: "Top 20 TV shows ranked by how often PremiereLens recommends them from related shows.",
    url: "https://premierelens.com/most-recommended",
    numberOfItems: shows.length,
    itemListElement: shows.slice(0, 50).map((show, index) => {
      const image = getStructuredDataImage(show);
      const description = getStructuredDataDescription(show);

      return {
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "TVSeries",
          name: show.title,
          url: `https://premierelens.com/show/${show.slug}`,
          ...(image ? { image } : {}),
          ...(description ? { description } : {}),
        },
      };
    }),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJson) }}
    />
  );
};

MostRecommendedStructuredData.propTypes = {
  shows: PropTypes.arrayOf(PropTypes.object),
};

const EvidenceRankingRow = React.memo(({ show, index }) => {
  const href = `/show/${show.slug}`;
  const proof = Array.isArray(show.recommendedBy) ? show.recommendedBy.slice(0, 4) : [];
  const summary = toSentence(consensusSummary(show));
  const sourceTags = Array.isArray(show.displayTasteProfile?.tags) && show.displayTasteProfile.tags.length > 0
    ? show.displayTasteProfile.tags
    : show.semanticEssence?.tasteTags;
  const tasteTags = Array.isArray(sourceTags) ? selectDisplayTasteTags(sourceTags, 3) : [];
  const trackConsensusClick = (target) => {
    trackEvent("recommendation_consensus_click", {
      show_slug: show.slug,
      rank: index + 1,
      target,
    });
  };

  return (
    <li className="py-5 first:pt-0 last:pb-0 md:py-6">
      <article className="most-recommended-row grid gap-3 rounded-lg lg:items-start">
        <div className="most-recommended-rank flex items-baseline gap-3 md:block">
          <span className="text-meta text-h3 font-semibold leading-none text-primary md:text-h2">
            {index + 1}
          </span>
          <span className="text-body-small font-medium text-muted-foreground md:mt-2 md:block">
            <RecommendationCountLabel count={show.inboundCount} />
          </span>
        </div>

        {show.poster ? (
          <a
            href={href}
            onClick={(event) => {
              trackConsensusClick("show");
              handleInternalLinkClick(event, href);
            }}
            className="most-recommended-poster overflow-hidden rounded-md bg-muted"
            aria-label={`Open ${show.title}`}
          >
            <img
              src={show.poster}
              alt={`${show.title} poster`}
              loading={index < 4 ? "eager" : "lazy"}
              className="aspect-[2/3] w-full object-cover transition-transform duration-normal ease-out hover:scale-105"
            />
          </a>
        ) : (
          <div className="most-recommended-poster aspect-[2/3] rounded-md bg-muted" aria-hidden="true" />
        )}

        <div className="most-recommended-content min-w-0">
          <Typography as="h3" variant="h3" className="leading-tight">
            <a
              href={href}
              onClick={(event) => {
                trackConsensusClick("show");
                handleInternalLinkClick(event, href);
              }}
              className="title-link"
            >
              {show.title}
            </a>
          </Typography>

          {show.hasShowsLikePage && (
            <a
              href={`/shows-like/${show.slug}`}
              onClick={(event) => {
                trackConsensusClick("shows_like");
                handleInternalLinkClick(event, `/shows-like/${show.slug}`);
              }}
              className="inline-flex text-body-small font-semibold text-primary underline-offset-2 hover:underline"
            >
              Shows like {show.title} →
            </a>
          )}

          <p className="most-recommended-copy reading-measure mt-3 text-body text-foreground/80">
            <BridgeText text={summary} titles={[show.title]} />
          </p>

          {tasteTags.length > 0 && (
            <ul className="mt-3 hidden max-w-3xl flex-wrap gap-2 sm:flex" aria-label={`${show.title} tags`}>
              {tasteTags.map((tag) => (
                <li key={tag}>
                  <TasteTag className="most-recommended-tag">
                    {tag}
                  </TasteTag>
                </li>
              ))}
            </ul>
          )}

          {proof.length > 0 && (
            <details
              className="most-recommended-details mt-4 border-t border-border/70 pt-3"
              onToggle={(event) => {
                if (!event.currentTarget.open) return;
                trackEvent("recommendation_evidence_open", {
                  show_slug: show.slug,
                  rank: index + 1,
                });
              }}
            >
              <summary className="cursor-pointer list-none text-body-small font-semibold text-foreground/75 transition-colors duration-fast ease-out hover:text-primary">
                See which shows lead here
              </summary>
              <div className="most-recommended-reasoning mt-4 space-y-4">
                {proof.map((source) => {
                  const sourceHref = `/show/${source.slug}`;
                  const bridge = String(source.bridge || "").replace(/\s+/g, " ").trim();
                  return (
                    <div key={source.slug} className="most-recommended-reason">
                      <p className="text-body-small font-semibold text-foreground">
                        <a
                          href={sourceHref}
                          onClick={(event) => handleInternalLinkClick(event, sourceHref)}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {source.title}
                        </a>
                      </p>
                      {bridge && (
                        <p className="most-recommended-bridge mt-2 text-body-small text-foreground/80">
                          <BridgeText text={bridge} titles={[source.title, show.title]} />
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </details>
          )}
        </div>
      </article>
    </li>
  );
});

EvidenceRankingRow.displayName = "EvidenceRankingRow";

EvidenceRankingRow.propTypes = {
  show: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    poster: PropTypes.string,
    inboundCount: PropTypes.number,
    semanticEssence: PropTypes.shape({
      essence: PropTypes.string,
      tasteTags: PropTypes.arrayOf(PropTypes.string),
    }),
    displayTasteProfile: PropTypes.shape({
      tags: PropTypes.arrayOf(PropTypes.string),
    }),
    hasShowsLikePage: PropTypes.bool,
    recommendedBy: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  index: PropTypes.number.isRequired,
};

const MostRecommendedPage = ({ initialMostRecommendedPayload = null }) => {
  const initialPayload = useMemo(
    () => resolveInitialPayload(initialMostRecommendedPayload, (payload) => Array.isArray(payload.data)),
    [initialMostRecommendedPayload]
  );
  const [payload, setPayload] = useState(() => initialPayload || null);
  const [loading, setLoading] = useState(() => !initialPayload);
  const [error, setError] = useState("");

  const loadShows = useCallback(async () => {
    try {
      setLoading(true);
      const nextPayload = await fetchMostRecommendedShows();
      setPayload(nextPayload);
      setError("");
    } catch (nextError) {
      setError(nextError.message || "Failed to load the most recommended shows.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialPayload) return;
    let active = true;
    const run = async () => {
      try {
        setLoading(true);
        const nextPayload = await fetchMostRecommendedShows();
        if (!active) return;
        setPayload(nextPayload);
        setError("");
      } catch (nextError) {
        if (!active) return;
        setError(nextError.message || "Failed to load the most recommended shows.");
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [initialPayload]);

  const shows = Array.isArray(payload?.data) ? payload.data.slice(0, MOST_RECOMMENDED_LIMIT) : [];
  const seoMeta = buildMostRecommendedSeoMeta({ data: shows });
  const updatedLabel = formatDisplayDate(payload?.lastUpdated) || null;
  usePageMeta(seoMeta);

  return (
    <div className="most-recommended-page min-h-screen bg-background text-foreground">
      <SubpageHeader backHref="/" backLabel="Home" />
      <MostRecommendedStructuredData shows={shows} />
      <main id="main-content" tabIndex={-1} className="route-shell route-shell--browse">
        <RecommendationsHub
          rankedShows={shows}
          updatedLabel={updatedLabel}
          rotationDate={payload?.lastUpdated || null}
        />

        {loading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="mx-auto mt-10 max-w-2xl rounded-lg border border-destructive/50 bg-destructive/10 p-5 text-center">
            <Typography as="h2" variant="h3" className="text-destructive">
              Could not load the ranking
            </Typography>
            <Typography variant="body" className="mx-auto mt-2 max-w-xl text-destructive/80">
              {error}
            </Typography>
            <div className="mt-5 flex justify-center">
              <Button type="button" variant="outline" onClick={loadShows}>
                Try again
              </Button>
            </div>
          </div>
        ) : shows.length === 0 ? (
          <section className="mt-10 rounded-lg border border-dashed border-accent-gold/70 bg-card/50 p-8 text-center">
            <Typography as="h2" variant="h3">
              No ranked shows yet
            </Typography>
            <Typography variant="body" className="mx-auto mt-2 max-w-xl text-muted-foreground">
              The ranking appears once enough recommendation links are available.
            </Typography>
          </section>
        ) : (
          <section className="mt-10" aria-labelledby="most-recommended-ranking-title">
            <div className="mb-5 max-w-3xl space-y-2">
              <Typography id="most-recommended-ranking-title" as="h2" variant="h2">
                Shows recommended most often
              </Typography>
              <Typography variant="body" className="reading-measure text-muted-foreground">
                These shows keep coming up across lots of different recommendations, so they suit you when you have no particular favourite in mind. Each was recommended after at least {payload?.minInbound || 3} different shows.
              </Typography>
            </div>
            <div className="most-recommended-ranking rounded-lg border border-border/80 bg-card/35">
              <ol className="most-recommended-list divide-y divide-border/70 border-t border-border/70 px-4 py-5 sm:px-5 md:px-6">
                {shows.map((show, index) => (
                  <EvidenceRankingRow key={show.slug} show={show} index={index} />
                ))}
              </ol>
            </div>
          </section>
        )}
      </main>
      <BackToTopButton />
      <SiteFooter />
    </div>
  );
};

MostRecommendedPage.propTypes = {
  initialMostRecommendedPayload: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.object),
    count: PropTypes.number,
    minInbound: PropTypes.number,
    lastUpdated: PropTypes.string,
  }),
};

export default MostRecommendedPage;
